<template>
  <div ref="root" class="events-sort-menu" :class="classModifiers">
    <sp-button
      class="events-sort-menu__trigger"
      type="button"
      color="custom"
      :disabled="disabled"
      aria-haspopup="dialog"
      :aria-expanded="isDialogOpen"
      :aria-controls="dialogId"
      @click="handleTriggerClick"
    >
      <sp-icon slot="prepend" class="events-sort-menu__trigger-icon" name="sort-alt" size="x-small"></sp-icon>
      <span class="events-sort-menu__trigger-label">{{ triggerLabel }}</span>
      <sp-icon
        slot="append"
        class="events-sort-menu__trigger-caret"
        name="chevron-down-filled"
        size="x-small"
      ></sp-icon>
    </sp-button>

    <dialog
      :id="dialogId"
      ref="dialog"
      class="events-sort-menu__dialog"
      :aria-labelledby="dialogTitleId"
      @cancel="handleDialogCancel"
      @close="handleDialogClose"
      @keydown.esc="handleDialogEscape"
      @click="handleDialogClick"
    >
      <h2 :id="dialogTitleId" class="events-sort-menu__dialog-title">{{ title }}</h2>
      <div class="events-sort-menu__options">
        <label v-for="option in options" :key="option.value" class="events-sort-menu__option">
          <input
            :checked="draft === option.value"
            class="events-sort-menu__option-input"
            type="radio"
            :name="optionInputName"
            :value="option.value"
            @change="handleOptionChange"
          />
          <span class="events-sort-menu__option-copy">
            <span class="events-sort-menu__option-label">{{ option.label }}</span>
            <span v-if="option.hint" class="events-sort-menu__option-hint">{{ option.hint }}</span>
          </span>
        </label>
      </div>
      <div class="events-sort-menu__actions">
        <sp-button class="events-sort-menu__done-button" type="button" color="primary" @click="applyAndClose">
          {{ closeText }}
        </sp-button>
      </div>
    </dialog>
  </div>
</template>

<script setup>
import { useEventListener, useMutationObserver } from "@vueuse/core";
import { computed, ref, useHost, useTemplateRef } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";

const emit = defineEmits(["select", "apply", "close"]);

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  draft: {
    type: String,
    default: "",
  },
  selectId: {
    type: String,
    required: true,
  },
  triggerLabel: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "Sort results by",
  },
  closeText: {
    type: String,
    default: "Done",
  },
});

const root = useTemplateRef("root");
const dialog = useTemplateRef("dialog");
const isDialogOpen = ref(false);
const lastFocusedElement = ref(null);

const host = useHost();
const { isSmScreen } = useBreakpoints();

const classModifiers = computed(() => (isSmScreen.value ? ["--is-sm-screen"] : []));
const optionInputName = computed(() => `events-sort-option-${props.selectId}`);
const dialogId = computed(() => `events-sort-dialog-${props.selectId}`);
const dialogTitleId = computed(() => `${dialogId.value}-title`);

useEventListener(document, "click", handleDocumentClick);

// AJAX detach removes the dialog from the top layer (drops ::backdrop). The
// open attribute persists, which makes showModal throw — clear it first, then
// re-add to the top layer. Reactive target detaches the observer when the
// sheet isn't open or we're on desktop, so it doesn't tax unrelated DOM churn.
const backdropObserverTarget = computed(() => (isSmScreen.value && isDialogOpen.value ? document.body : null));

useMutationObserver(
  backdropObserverTarget,
  () => {
    if (!dialog.value) {
      return;
    }
    let isAlreadyModal = false;
    try {
      isAlreadyModal = dialog.value.matches(":modal");
    } catch {
      // jsdom doesn't support :modal
    }
    if (isAlreadyModal) {
      return;
    }
    try {
      dialog.value.removeAttribute("open");
      dialog.value.showModal();
    } catch {
      // Transient state; next mutation retries.
    }
  },
  { childList: true, subtree: true },
);

function showDialog() {
  if (!dialog.value) {
    return;
  }
  if (dialog.value.open) {
    return;
  }

  if (isSmScreen.value) {
    dialog.value.showModal();
  } else {
    dialog.value.show();
  }

  isDialogOpen.value = true;
  requestAnimationFrame(focusActiveOption);
}

function handleTriggerClick(event) {
  lastFocusedElement.value = event?.currentTarget ?? document.activeElement;

  if (dialog.value?.open) {
    closeDialog();
  } else {
    showDialog();
  }
}

function closeDialog({ restoreFocus = true } = {}) {
  if (!dialog.value || !dialog.value.open) {
    return;
  }

  if (!restoreFocus) {
    lastFocusedElement.value = null;
  }
  dialog.value.close();
}

function applyAndClose() {
  emit("apply");
  closeDialog();
}

function handleDialogClick(event) {
  if (event.target === dialog.value) {
    closeDialog();
  }
}

function handleDialogCancel(event) {
  event.preventDefault();
  closeDialog();
}

function handleDialogClose() {
  isDialogOpen.value = false;
  emit("close");
  restoreFocusAfterClose();
}

function handleDialogEscape() {
  closeDialog();
}

function handleDocumentClick(event) {
  if (!dialog.value?.open || isSmScreen.value) {
    return;
  }

  if (!eventOriginatedInsideComponent(event)) {
    closeDialog({ restoreFocus: false });
  }
}

function eventOriginatedInsideComponent(event) {
  const path = event.composed ? event.composedPath() : null;
  const clickedInsideInnerContent = path ? path.includes(root.value) : root.value?.contains(event.target);
  const clickedInsideCustomElementHost = path
    ? Boolean(host && path.includes(host))
    : Boolean(host?.contains(event.target));

  return clickedInsideInnerContent || clickedInsideCustomElementHost;
}

function handleOptionChange(event) {
  emit("select", event.target.value);
}

function focusActiveOption() {
  if (!dialog.value?.open) {
    return;
  }

  const selectedOptionInput = dialog.value.querySelector("input.events-sort-menu__option-input:checked");
  const fallbackOptionInput = dialog.value.querySelector("input.events-sort-menu__option-input");
  const focusTarget = selectedOptionInput || fallbackOptionInput || dialog.value;
  focusTarget?.focus();
}

function restoreFocusAfterClose() {
  const focusTarget = lastFocusedElement.value;
  if (focusTarget?.isConnected && typeof focusTarget.focus === "function") {
    focusTarget.focus();
  }

  lastFocusedElement.value = null;
}

defineExpose({
  close: closeDialog,
});
</script>

<style lang="scss" scoped>
.events-sort-menu {
  &__trigger {
    --sp-comp-button-custom-bg-color: transparent;
    --sp-comp-button-custom-text-color: var(--sp-sys-color-primary);
    --sp-comp-button-custom-hover-text-color: var(--sp-sys-color-primary);
    --sp-comp-button-border-color: var(--sp-sys-color-outline-variant);
    --sp-comp-button-border-width: 1px;
    --sp-comp-button-border-radius: var(--sp-sys-form-field-border-radius);
    --sp-comp-button-min-width: 0;
    --sp-comp-button-size: auto;
    --sp-comp-button-gap: var(--sp-ref-scale-6);
    --sp-comp-button-padding: var(--sp-ref-scale-6) var(--sp-ref-scale-16);
    --sp-comp-button-font-short: var(--sp-sys-typescale-body-semi-bold-short);
    --sp-comp-button-prepend-icon-size: var(--sp-sys-icon-size-small);
    --sp-comp-button-append-icon-size: var(--sp-sys-icon-size-small);

    cursor: pointer;
    margin: 0;
    white-space: nowrap;
  }

  &__dialog {
    border: var(--sp-sys-form-field-border);
    border-radius: var(--sp-sys-form-field-border-radius);
    box-shadow: var(--sp-ref-shadow-2);
    box-sizing: border-box;
    bottom: auto;
    left: 0;
    margin: 0;
    overflow-y: visible;
    padding: var(--sp-ref-scale-20) var(--sp-ref-scale-24) var(--sp-ref-scale-40);
    position: absolute;
    right: auto;
    top: calc(100% + var(--sp-ref-scale-4));
    width: 20rem;
    z-index: var(--sp-sys-z-index-max);
  }

  &__dialog-title {
    color: var(--sp-ref-palette-text);
    display: block;
    font: var(--sp-sys-typescale-body-bold-short);
    margin: 0 0 var(--sp-ref-scale-14);
  }

  &__options {
    display: block;
  }

  &__option {
    color: var(--sp-ref-palette-text);
    cursor: pointer;
    display: grid;
    column-gap: 0.5ch;
    row-gap: 0;
    grid-template-columns: auto 1fr;

    &:not(:last-child) {
      margin-bottom: var(--sp-ref-scale-10);
    }
  }

  &__option-input {
    accent-color: var(--sp-sys-color-primary);
    margin: 0;
  }

  &__option-copy {
    display: contents;
  }

  &__option-label {
    font: var(--sp-sys-typescale-body-semi-bold-short);
  }

  &__option-hint {
    color: inherit;
    font: var(--sp-sys-typescale-body-small-short);
    grid-column: 2;
  }

  &__actions {
    display: none;
  }

  &__done-button {
    --button-width: 100%;
    --button-min-width: 0;
    --button-min-height: var(--sp-comp-button-size);
    --button-border-radius: var(--sp-sys-form-field-border-radius);
  }

  &.--is-sm-screen {
    .events-sort-menu__trigger {
      --sp-comp-button-border-width: 0;
      --sp-comp-button-gap: 0;
      --sp-comp-button-padding: 0;
    }

    .events-sort-menu__dialog {
      border: 0;
      border-radius: var(--sp-ref-scale-8) var(--sp-ref-scale-8) 0 0;
      box-shadow: none;
      inset: auto 0 0 0;
      max-width: 100vw;
      max-height: 85vh;
      overflow-y: auto;
      padding: var(--sp-ref-scale-16) var(--sp-ref-scale-16) 0;
      position: fixed;
      width: 100vw;

      &::backdrop {
        background-color: var(--sp-comp-events-sort-backdrop-background-color, #000);
        opacity: var(--sp-comp-events-sort-backdrop-opacity, 0.3);
      }
    }

    .events-sort-menu__actions {
      display: block;
      margin-top: var(--sp-ref-scale-16);
      padding: var(--sp-ref-scale-14) 0;
    }
  }
}
</style>
